// =============================================================================
// SIREPPAR - Stepper de fase de un expediente de RECONOCIMIENTO
// =============================================================================
// Reemplaza al TimelineEstado (basado en `estado`) dentro del detalle del
// expediente cuando el tipo es RECONOCIMIENTO. Muestra las 6 etapas generales
// y el sub-estado actual (fase). Los botones de acción viven arriba a la
// derecha, junto al resto de las acciones del expediente.
// =============================================================================

import {
  ETAPAS_RECONOCIMIENTO,
  etapaIndexDeFase,
  getFaseActual,
  faseLabel,
  faseTono,
  faseDescripcion,
  type FaseReconocimiento,
  type FaseTono,
} from '../../utils/fasesReconocimiento';

interface Props {
  expediente: {
    id: string;
    datosFormulario?: { fase?: string; atributosRechazados?: string[] } | null;
  };
}

const TONO_BADGE: Record<FaseTono, string> = {
  pend: 'bg-amber-100 text-amber-700 border-amber-200',
  info: 'bg-blue-100 text-blue-700 border-blue-200',
  obs: 'bg-orange-100 text-orange-700 border-orange-200',
  bad: 'bg-red-100 text-red-700 border-red-200',
  ok: 'bg-green-100 text-green-700 border-green-200',
};

export default function ReconocimientoFasePanel({ expediente }: Props) {
  const df = expediente.datosFormulario ?? {};
  const fase = (getFaseActual(df) ?? 'WIZARD_EXTERNO_COMPLETO') as FaseReconocimiento;
  const idxActual = etapaIndexDeFase(fase);
  const atributosRechazados = df.atributosRechazados ?? [];
  const esRechazado = fase === 'ATRIBUTOS_RECHAZADOS' || fase === 'DOCUMENTACION_RECHAZADA';

  return (
    <div className="space-y-3">
      {/* Stepper de 6 etapas (misma estética que TimelineEstado) */}
      <div className="flex items-center">
        {ETAPAS_RECONOCIMIENTO.map((etapa, idx) => (
          <div key={idx} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium border-2 ${
                  esRechazado && idx === idxActual
                    ? 'bg-red-100 border-red-500 text-red-700'
                    : idx < idxActual
                    ? 'bg-green-500 border-green-500 text-white'
                    : idx === idxActual
                    ? 'bg-orange-100 border-orange-500 text-orange-700'
                    : 'bg-gray-50 border-gray-300 text-gray-400'
                }`}
              >
                {idx < idxActual ? '✓' : idx + 1}
              </div>
              <span
                className={`text-xs mt-1 whitespace-nowrap ${
                  idx === idxActual ? 'font-medium text-orange-700' : idx < idxActual ? 'text-green-600' : 'text-gray-400'
                }`}
              >
                {etapa}
              </span>
            </div>
            {idx < ETAPAS_RECONOCIMIENTO.length - 1 && (
              <div className={`flex-1 h-0.5 mx-1 mb-4 ${idx < idxActual ? 'bg-green-400' : 'bg-gray-200'}`} />
            )}
          </div>
        ))}
      </div>

      {/* Sub-estado actual */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-gray-500">Estado actual:</span>
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold border ${TONO_BADGE[faseTono(fase)]}`}>
          {faseLabel(fase)}
        </span>
        {fase === 'ATRIBUTOS_RECHAZADOS' && atributosRechazados.length > 0 && (
          <span className="text-sm text-red-600">· A corregir: {atributosRechazados.join(', ')}</span>
        )}
      </div>

      {/* Explicación en texto plano (sin color) de qué significa el estado */}
      <p className="text-sm text-gray-600">{faseDescripcion(fase)}</p>
    </div>
  );
}
